import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Building2, Home as HomeIcon, EyeOff, MoveVertical, Save, ArrowUp, ArrowDown, Palette, Image as ImageIcon, MousePointer, History, RotateCcw, Upload, FileText, LayoutTemplate, GripVertical, Eye, Pencil } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../ui/alert-dialog';
import { DndContext, PointerSensor, useSensor, useSensors, closestCenter } from '@dnd-kit/core';
import { SortableContext, useSortable, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import PageEditor from '../builder/PageEditor';
import AdminDesign from './AdminDesign';
import { API, authHeaders, apiErr } from './adminApi';

const STATUS_OPTIONS = [
  { value: 'published', label: 'Published' },
  { value: 'hidden', label: 'Hidden' },
  { value: 'coming_soon', label: 'Coming Soon' },
  { value: 'under_construction', label: 'Under Construction' },
];

const AdminWebsite = ({ onUnauthorized }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState({});
  const [editorOpen, setEditorOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/admin/site-settings`, { headers: authHeaders() });
        setSettings(data);
      } catch (err) {
        if (err?.response?.status === 401) onUnauthorized?.();
        else toast.error(`Failed to load: ${apiErr(err)}`);
      } finally { setLoading(false); }
    })();
  }, [onUnauthorized]);

  const patchAndSave = async (patch, toastMsg = 'Saved') => {
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API}/api/admin/site-settings`, patch, { headers: authHeaders() });
      setSettings(data);
      setDirty({});
      toast.success(toastMsg);
    } catch (err) {
      toast.error(`Save failed: ${apiErr(err)}`);
    } finally { setSaving(false); }
  };

  if (loading || !settings) {
    return <div className="text-gray-400 text-sm">Loading website settings…</div>;
  }

  return (
    <div className="space-y-4" data-testid="admin-website">
      {editorOpen && <PageEditor pageKey="home" onClose={() => setEditorOpen(false)} />}
      <Tabs defaultValue="pages" className="w-full">
        <TabsList className="bg-slate-900 border border-slate-800 flex-wrap h-auto mb-4">
          <TabsTrigger value="pages" data-testid="ws-tab-pages-mgmt" className="data-[state=active]:bg-red-600 data-[state=active]:text-white"><LayoutTemplate className="w-4 h-4 mr-2" /> Pages</TabsTrigger>
          <TabsTrigger value="design" data-testid="ws-tab-design" className="data-[state=active]:bg-red-600 data-[state=active]:text-white"><Palette className="w-4 h-4 mr-2" /> Design</TabsTrigger>
          <TabsTrigger value="business" data-testid="ws-tab-business" className="data-[state=active]:bg-red-600 data-[state=active]:text-white"><Building2 className="w-4 h-4 mr-2" /> Business info</TabsTrigger>
          <TabsTrigger value="homepage" data-testid="ws-tab-homepage" className="data-[state=active]:bg-red-600 data-[state=active]:text-white"><HomeIcon className="w-4 h-4 mr-2" /> Homepage</TabsTrigger>
          <TabsTrigger value="branding" data-testid="ws-tab-branding" className="data-[state=active]:bg-red-600 data-[state=active]:text-white"><ImageIcon className="w-4 h-4 mr-2" /> Branding</TabsTrigger>
          <TabsTrigger value="background" data-testid="ws-tab-background" className="data-[state=active]:bg-red-600 data-[state=active]:text-white"><ImageIcon className="w-4 h-4 mr-2" /> Background</TabsTrigger>
          <TabsTrigger value="cursor" data-testid="ws-tab-cursor" className="data-[state=active]:bg-red-600 data-[state=active]:text-white"><MousePointer className="w-4 h-4 mr-2" /> Cursor</TabsTrigger>
          <TabsTrigger value="visibility" data-testid="ws-tab-visibility" className="data-[state=active]:bg-red-600 data-[state=active]:text-white"><EyeOff className="w-4 h-4 mr-2" /> Page visibility</TabsTrigger>
          <TabsTrigger value="nav" data-testid="ws-tab-nav" className="data-[state=active]:bg-red-600 data-[state=active]:text-white"><MoveVertical className="w-4 h-4 mr-2" /> Navigation</TabsTrigger>
          <TabsTrigger value="history" data-testid="ws-tab-history" className="data-[state=active]:bg-red-600 data-[state=active]:text-white"><History className="w-4 h-4 mr-2" /> History</TabsTrigger>
        </TabsList>

        <TabsContent value="pages"><PagesManager onEditHome={() => setEditorOpen(true)} /></TabsContent>
        <TabsContent value="design"><AdminDesign settings={settings} onSave={patchAndSave} saving={saving} /></TabsContent>
        <TabsContent value="business"><BusinessInfoEditor settings={settings} onSave={patchAndSave} saving={saving} dirty={dirty} setDirty={setDirty} /></TabsContent>
        <TabsContent value="homepage"><HomepageEditor settings={settings} onSave={patchAndSave} saving={saving} dirty={dirty} setDirty={setDirty} /></TabsContent>
        <TabsContent value="branding"><BrandingEditor settings={settings} onSave={patchAndSave} saving={saving} /></TabsContent>
        <TabsContent value="background"><BackgroundEditor settings={settings} onSave={patchAndSave} saving={saving} /></TabsContent>
        <TabsContent value="cursor"><CursorEditor settings={settings} onSave={patchAndSave} saving={saving} /></TabsContent>
        <TabsContent value="visibility"><PageVisibilityEditor settings={settings} onSave={patchAndSave} saving={saving} /></TabsContent>
        <TabsContent value="nav"><NavigationEditor settings={settings} onSave={patchAndSave} saving={saving} /></TabsContent>
        <TabsContent value="history"><HistoryPanel onRestored={setSettings} /></TabsContent>
      </Tabs>
    </div>
  );
};

// -------- Pages manager (entry point to the visual builder) --------
const PagesManager = ({ onEditHome }) => (
  <Card className="bg-slate-900 border-slate-800" data-testid="pages-manager">
    <CardHeader>
      <CardTitle className="text-white">Pages</CardTitle>
      <p className="text-xs text-gray-400">Open the visual builder to drag, edit, and arrange your homepage blocks. Changes stay in a draft until you Publish.</p>
    </CardHeader>
    <CardContent className="space-y-2">
      <div className="flex items-center justify-between bg-slate-950/40 border border-slate-800 rounded-md p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-red-600/10 flex items-center justify-center"><HomeIcon className="w-5 h-5 text-red-500" /></div>
          <div>
            <div className="text-white font-semibold">Home</div>
            <div className="text-xs text-gray-500 font-mono">/</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <a href="/?preview=1" target="_blank" rel="noreferrer">
            <Button size="sm" variant="outline" data-testid="pages-preview-home" className="border-slate-700 text-white hover:bg-slate-800"><Eye className="w-4 h-4 mr-1.5" /> Preview</Button>
          </a>
          <Button size="sm" data-testid="pages-edit-home" onClick={onEditHome} className="bg-red-600 hover:bg-red-700 text-white"><Pencil className="w-4 h-4 mr-1.5" /> Edit Page</Button>
        </div>
      </div>
      <p className="text-[11px] text-gray-500 px-1">More pages (Services, About, Contact) can be added to the builder in a future update.</p>
    </CardContent>
  </Card>
);

// -------- Business Info --------
const BusinessInfoEditor = ({ settings, onSave, saving, dirty, setDirty }) => {
  const b = { ...settings.business, ...(dirty.business || {}) };
  const set = (k) => (e) => setDirty((d) => ({ ...d, business: { ...(d.business || {}), [k]: e.target.value } }));
  const hasChanges = Boolean(dirty.business);
  const save = () => onSave({ business: b }, 'Business info updated everywhere');
  return (
    <Card className="bg-slate-900 border-slate-800">
      <CardHeader>
        <CardTitle className="text-white">Business information</CardTitle>
        <p className="text-xs text-gray-400">Update once here. Changes appear in the header, footer, contact section, PDFs, and emails.</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <F label="Business name"><Input data-testid="ws-biz-name" value={b.name || ''} onChange={set('name')} className="bg-slate-800 border-slate-700 text-white" /></F>
          <F label="Tagline"><Input data-testid="ws-biz-tagline" value={b.tagline || ''} onChange={set('tagline')} className="bg-slate-800 border-slate-700 text-white" /></F>
          <F label="Phone"><Input data-testid="ws-biz-phone" value={b.phone || ''} onChange={set('phone')} className="bg-slate-800 border-slate-700 text-white" /></F>
          <F label="Email"><Input data-testid="ws-biz-email" type="email" value={b.email || ''} onChange={set('email')} className="bg-slate-800 border-slate-700 text-white" /></F>
          <F label="Address line 1"><Input data-testid="ws-biz-address1" value={b.address_line1 || ''} onChange={set('address_line1')} className="bg-slate-800 border-slate-700 text-white" /></F>
          <F label="Address line 2"><Input data-testid="ws-biz-address2" value={b.address_line2 || ''} onChange={set('address_line2')} className="bg-slate-800 border-slate-700 text-white" /></F>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <F label="Weekday hours"><Input data-testid="ws-biz-hours-weekday" value={b.hours_weekday || ''} onChange={set('hours_weekday')} className="bg-slate-800 border-slate-700 text-white" /></F>
          <F label="Saturday hours"><Input data-testid="ws-biz-hours-saturday" value={b.hours_saturday || ''} onChange={set('hours_saturday')} className="bg-slate-800 border-slate-700 text-white" /></F>
          <F label="Sunday hours"><Input data-testid="ws-biz-hours-sunday" value={b.hours_sunday || ''} onChange={set('hours_sunday')} className="bg-slate-800 border-slate-700 text-white" /></F>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <F label="Google Maps URL"><Input data-testid="ws-biz-maps" value={b.google_maps_url || ''} onChange={set('google_maps_url')} placeholder="https://maps.google.com/…" className="bg-slate-800 border-slate-700 text-white" /></F>
          <F label="Facebook URL"><Input data-testid="ws-biz-facebook" value={b.facebook_url || ''} onChange={set('facebook_url')} placeholder="https://facebook.com/…" className="bg-slate-800 border-slate-700 text-white" /></F>
          <F label="Instagram URL"><Input data-testid="ws-biz-instagram" value={b.instagram_url || ''} onChange={set('instagram_url')} placeholder="https://instagram.com/…" className="bg-slate-800 border-slate-700 text-white" /></F>
        </div>
        <div className="flex justify-end">
          <Button data-testid="ws-biz-save" onClick={save} disabled={saving || !hasChanges} className="bg-red-600 hover:bg-red-700 text-white">
            <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving…' : 'Save business info'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// -------- Homepage --------
const HomepageEditor = ({ settings, onSave, saving, dirty, setDirty }) => {
  const h = { ...settings.homepage, ...(dirty.homepage || {}) };
  const set = (k) => (e) => setDirty((d) => ({ ...d, homepage: { ...(d.homepage || {}), [k]: e.target.value } }));
  const hasChanges = Boolean(dirty.homepage);
  const save = () => onSave({ homepage: h }, 'Homepage updated');

  const moveSection = (idx, dir) => {
    const sections = [...(h.sections || [])];
    const j = idx + dir;
    if (j < 0 || j >= sections.length) return;
    [sections[idx], sections[j]] = [sections[j], sections[idx]];
    sections.forEach((s, i) => { s.order = i + 1; });
    setDirty((d) => ({ ...d, homepage: { ...(d.homepage || {}), ...h, sections } }));
  };
  const toggleSection = (idx) => {
    const sections = [...(h.sections || [])];
    sections[idx] = { ...sections[idx], visible: !sections[idx].visible };
    setDirty((d) => ({ ...d, homepage: { ...(d.homepage || {}), ...h, sections } }));
  };

  return (
    <div className="space-y-4">
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-white">Hero section</CardTitle>
          <p className="text-xs text-gray-400">Main headline and call-to-action buttons at the top of the homepage.</p>
        </CardHeader>
        <CardContent className="space-y-3">
          <F label="Headline">
            <Input data-testid="ws-hp-headline" value={h.headline || ''} onChange={set('headline')} className="bg-slate-800 border-slate-700 text-white" />
          </F>
          <F label="Subline (short description below headline)">
            <Textarea data-testid="ws-hp-subline" value={h.subline || ''} onChange={set('subline')} rows={2} className="bg-slate-800 border-slate-700 text-white" />
          </F>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <F label="Primary button text"><Input data-testid="ws-hp-cta1-text" value={h.primary_cta_text || ''} onChange={set('primary_cta_text')} className="bg-slate-800 border-slate-700 text-white" /></F>
            <F label="Primary button link"><Input data-testid="ws-hp-cta1-link" value={h.primary_cta_link || ''} onChange={set('primary_cta_link')} className="bg-slate-800 border-slate-700 text-white font-mono text-sm" /></F>
            <F label="Secondary button text"><Input data-testid="ws-hp-cta2-text" value={h.secondary_cta_text || ''} onChange={set('secondary_cta_text')} className="bg-slate-800 border-slate-700 text-white" /></F>
            <F label="Secondary button link"><Input data-testid="ws-hp-cta2-link" value={h.secondary_cta_link || ''} onChange={set('secondary_cta_link')} className="bg-slate-800 border-slate-700 text-white font-mono text-sm" /></F>
          </div>
          <F label="Hero image (URL or /path/in/public)">
            <Input data-testid="ws-hp-hero-image" value={h.hero_image_url || ''} onChange={set('hero_image_url')} className="bg-slate-800 border-slate-700 text-white font-mono text-sm" />
          </F>
        </CardContent>
      </Card>

      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-white">Homepage sections</CardTitle>
          <p className="text-xs text-gray-400">Show, hide, or reorder the blocks on the homepage. Hidden sections stay saved for later.</p>
        </CardHeader>
        <CardContent className="space-y-2">
          {(h.sections || []).map((s, idx) => (
            <div key={s.key} data-testid={`ws-hp-section-${s.key}`} className="flex items-center justify-between bg-slate-950/40 border border-slate-800 rounded-md p-3">
              <div className="flex items-center gap-3">
                <div className="w-6 text-center text-xs text-gray-500 font-mono">{idx + 1}</div>
                <div>
                  <div className="text-white text-sm font-semibold">{s.label}</div>
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 mt-0.5">{s.key}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button size="icon" variant="outline" data-testid={`ws-hp-section-up-${s.key}`} onClick={() => moveSection(idx, -1)} disabled={idx === 0} className="border-slate-700 text-white hover:bg-slate-800 w-8 h-8"><ArrowUp className="w-3 h-3" /></Button>
                <Button size="icon" variant="outline" data-testid={`ws-hp-section-down-${s.key}`} onClick={() => moveSection(idx, 1)} disabled={idx === (h.sections || []).length - 1} className="border-slate-700 text-white hover:bg-slate-800 w-8 h-8"><ArrowDown className="w-3 h-3" /></Button>
                <Switch data-testid={`ws-hp-section-toggle-${s.key}`} checked={s.visible} onCheckedChange={() => toggleSection(idx)} />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button data-testid="ws-hp-save" onClick={save} disabled={saving || !hasChanges} className="bg-red-600 hover:bg-red-700 text-white">
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving…' : 'Save homepage'}
        </Button>
      </div>
    </div>
  );
};

// -------- Page Visibility --------
const PageVisibilityEditor = ({ settings, onSave, saving }) => {
  const [local, setLocal] = useState(settings.pages || []);
  useEffect(() => { setLocal(settings.pages || []); }, [settings.pages]);

  const updatePage = (idx, patch) => {
    setLocal((prev) => prev.map((p, i) => (i === idx ? { ...p, ...patch } : p)));
  };
  const save = () => onSave({ pages: local }, 'Page visibility updated');
  const hasChanges = JSON.stringify(local) !== JSON.stringify(settings.pages || []);

  return (
    <Card className="bg-slate-900 border-slate-800">
      <CardHeader>
        <CardTitle className="text-white">Public page visibility</CardTitle>
        <p className="text-xs text-gray-400">Hide unfinished pages, put them in Coming Soon, or show an Under Construction notice.</p>
      </CardHeader>
      <CardContent className="space-y-2">
        {local.map((p, idx) => (
          <div key={p.key} data-testid={`ws-page-${p.key}`} className="bg-slate-950/40 border border-slate-800 rounded-md p-3 space-y-2">
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div className="min-w-0">
                <div className="text-white text-sm font-semibold">{p.label}</div>
                <div className="text-xs text-gray-500 font-mono">{p.path}</div>
              </div>
              <select
                data-testid={`ws-page-status-${p.key}`}
                value={p.status}
                onChange={(e) => updatePage(idx, { status: e.target.value })}
                className="bg-slate-800 border border-slate-700 text-white rounded px-3 py-1.5 text-sm"
              >
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            {(p.status === 'coming_soon' || p.status === 'under_construction') && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2 pt-2 border-t border-slate-800">
                <Input data-testid={`ws-page-heading-${p.key}`} value={p.coming_soon_heading || ''} placeholder="Heading" onChange={(e) => updatePage(idx, { coming_soon_heading: e.target.value })} className="bg-slate-800 border-slate-700 text-white text-sm" />
                <Input data-testid={`ws-page-message-${p.key}`} value={p.coming_soon_message || ''} placeholder="Message" onChange={(e) => updatePage(idx, { coming_soon_message: e.target.value })} className="bg-slate-800 border-slate-700 text-white text-sm" />
              </div>
            )}
          </div>
        ))}
        <div className="flex justify-end pt-2">
          <Button data-testid="ws-pages-save" onClick={save} disabled={saving || !hasChanges} className="bg-red-600 hover:bg-red-700 text-white">
            <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving…' : 'Save page visibility'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// -------- Navigation --------
const NavigationEditor = ({ settings, onSave, saving }) => {
  const [local, setLocal] = useState(settings.navigation || []);
  useEffect(() => { setLocal((settings.navigation || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0))); }, [settings.navigation]);

  const update = (idx, patch) => setLocal((prev) => prev.map((n, i) => (i === idx ? { ...n, ...patch } : n)));
  const save = () => onSave({ navigation: local.map((n, i) => ({ ...n, order: i + 1 })) }, 'Navigation updated');
  const hasChanges = JSON.stringify(local) !== JSON.stringify(settings.navigation || []);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    setLocal((prev) => {
      const from = prev.findIndex((n) => n.key === active.id);
      const to = prev.findIndex((n) => n.key === over.id);
      if (from < 0 || to < 0) return prev;
      return arrayMove(prev, from, to).map((n, i) => ({ ...n, order: i + 1 }));
    });
  };

  return (
    <Card className="bg-slate-900 border-slate-800">
      <CardHeader>
        <CardTitle className="text-white">Header &amp; footer navigation</CardTitle>
        <p className="text-xs text-gray-400">Rename menu items, change where they link, or hide items. Order is top-to-bottom = left-to-right in the header.</p>
      </CardHeader>
      <CardContent className="space-y-2">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={local.map((n) => n.key)} strategy={verticalListSortingStrategy}>
            <div className="space-y-2">
              {local.map((n, idx) => (
                <NavRow key={n.key} item={n} onLabel={(v) => update(idx, { label: v })} onPath={(v) => update(idx, { path: v })} onVisible={(v) => update(idx, { visible: v })} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
        <div className="flex justify-end pt-2">
          <Button data-testid="ws-nav-save" onClick={save} disabled={saving || !hasChanges} className="bg-red-600 hover:bg-red-700 text-white">
            <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving…' : 'Save navigation'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const NavRow = ({ item, onLabel, onPath, onVisible }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.key });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  return (
    <div ref={setNodeRef} style={style} data-testid={`ws-nav-${item.key}`} className="grid grid-cols-12 items-center gap-2 bg-slate-950/40 border border-slate-800 rounded-md p-2">
      <div className="col-span-1 flex justify-center">
        <button {...attributes} {...listeners} data-testid={`ws-nav-drag-${item.key}`} className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-white touch-none"><GripVertical className="w-4 h-4" /></button>
      </div>
      <div className="col-span-4"><Input data-testid={`ws-nav-label-${item.key}`} value={item.label} onChange={(e) => onLabel(e.target.value)} placeholder="Label" className="bg-slate-800 border-slate-700 text-white h-8 text-sm" /></div>
      <div className="col-span-5"><Input data-testid={`ws-nav-path-${item.key}`} value={item.path} onChange={(e) => onPath(e.target.value)} placeholder="/path or /#anchor" className="bg-slate-800 border-slate-700 text-white font-mono text-xs h-8" /></div>
      <div className="col-span-2 flex items-center justify-end gap-2">
        <Switch data-testid={`ws-nav-visible-${item.key}`} checked={item.visible} onCheckedChange={onVisible} />
        <span className="text-xs text-gray-500">{item.visible ? 'Shown' : 'Hidden'}</span>
      </div>
    </div>
  );
};


// -------- Branding --------
const COLOR_THEMES = [
  { value: 'navy_red', label: 'Navy + Red (current)', swatches: ['#0f172a', '#dc2626'] },
  { value: 'navy_gold', label: 'Navy + Gold', swatches: ['#0f172a', '#f59e0b'] },
  { value: 'navy_silver', label: 'Navy + Silver', swatches: ['#0f172a', '#94a3b8'] },
  { value: 'navy_forest', label: 'Navy + Forest Green', swatches: ['#0f172a', '#15803d'] },
];
const FONT_PAIRS = [
  { value: 'system_sans', label: 'System sans-serif (current)', example: 'Aa · The quick brown fox' },
  { value: 'serif_display', label: 'Serif display', example: 'Aa · The quick brown fox' },
  { value: 'condensed_bold', label: 'Condensed bold', example: 'AA · THE QUICK BROWN FOX' },
];

const BrandingEditor = ({ settings, onSave, saving }) => {
  const [local, setLocal] = useState(settings.theme?.branding || {});
  useEffect(() => { setLocal(settings.theme?.branding || {}); }, [settings.theme?.branding]);
  const set = (k) => (e) => setLocal((v) => ({ ...v, [k]: e.target.value }));
  const dirty = JSON.stringify(local) !== JSON.stringify(settings.theme?.branding || {});
  const save = () => onSave({ theme: { ...settings.theme, branding: local } }, 'Branding updated');
  return (
    <div className="space-y-4">
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-white">Logos &amp; favicon</CardTitle>
          <p className="text-xs text-gray-400">URL or /path/in/public. The primary logo shows on the homepage hero. Light/dark variants are optional.</p>
        </CardHeader>
        <CardContent className="space-y-3">
          <F label="Primary logo URL"><Input data-testid="ws-brand-logo-primary" value={local.logo_primary || ''} onChange={set('logo_primary')} className="bg-slate-800 border-slate-700 text-white font-mono text-sm" /></F>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <F label="Light background logo (optional)"><Input data-testid="ws-brand-logo-light" value={local.logo_light || ''} onChange={set('logo_light')} className="bg-slate-800 border-slate-700 text-white font-mono text-sm" /></F>
            <F label="Dark variant logo (optional)"><Input data-testid="ws-brand-logo-dark" value={local.logo_dark || ''} onChange={set('logo_dark')} className="bg-slate-800 border-slate-700 text-white font-mono text-sm" /></F>
          </div>
          <F label="Favicon URL"><Input data-testid="ws-brand-favicon" value={local.favicon || ''} onChange={set('favicon')} className="bg-slate-800 border-slate-700 text-white font-mono text-sm" /></F>
          {local.logo_primary && (
            <div className="pt-2">
              <span className="text-xs text-gray-500 block mb-1">Preview</span>
              <div className="bg-slate-950 border border-slate-800 rounded-md p-4 flex items-center justify-center">
                <img src={local.logo_primary} alt="Logo preview" className="max-h-24 object-contain" />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-white">Color theme</CardTitle>
          <p className="text-xs text-gray-400">Pick from approved combinations — no CSS knobs. Navy stays the base.</p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {COLOR_THEMES.map((c) => {
              const active = local.color_theme === c.value;
              return (
                <button key={c.value} type="button" data-testid={`ws-brand-color-${c.value}`}
                  onClick={() => setLocal((v) => ({ ...v, color_theme: c.value }))}
                  className={`text-left rounded-md p-3 transition-all border-2 flex items-center justify-between ${active ? 'border-red-500 bg-slate-950' : 'border-slate-800 bg-slate-950/40 hover:border-slate-700'}`}>
                  <div>
                    <div className="text-white font-semibold text-sm">{c.label}</div>
                    <div className="text-[10px] text-gray-500 mt-0.5">{active ? 'ACTIVE' : 'Click to select'}</div>
                  </div>
                  <div className="flex gap-1.5">
                    {c.swatches.map((s) => <div key={s} className="w-6 h-6 rounded-full border border-slate-700" style={{ background: s }} />)}
                  </div>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-white">Font pairing</CardTitle>
          <p className="text-xs text-gray-400">Three approved font pairings. Choose the one that matches your brand voice.</p>
        </CardHeader>
        <CardContent className="space-y-2">
          {FONT_PAIRS.map((f) => {
            const active = local.font_pair === f.value;
            return (
              <button key={f.value} type="button" data-testid={`ws-brand-font-${f.value}`}
                onClick={() => setLocal((v) => ({ ...v, font_pair: f.value }))}
                className={`w-full text-left rounded-md p-3 transition-all border-2 ${active ? 'border-red-500 bg-slate-950' : 'border-slate-800 bg-slate-950/40 hover:border-slate-700'}`}>
                <div className="flex items-center justify-between">
                  <div className="text-white font-semibold text-sm">{f.label}</div>
                  <div className="text-[10px] text-gray-500">{active ? 'ACTIVE' : 'Select'}</div>
                </div>
                <div className={`mt-2 text-lg ${f.value === 'serif_display' ? 'font-serif' : f.value === 'condensed_bold' ? 'font-black uppercase tracking-wider' : 'font-medium'}`}>{f.example}</div>
              </button>
            );
          })}
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button data-testid="ws-branding-save" onClick={save} disabled={saving || !dirty} className="bg-red-600 hover:bg-red-700 text-white">
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving…' : 'Save branding'}
        </Button>
      </div>
    </div>
  );
};

// -------- Background --------
const BG_VARIANTS = [
  { value: 'standard_navy', label: 'Standard Navy', hint: 'Clean, solid dark navy background' },
  { value: 'ghost_flag', label: 'Navy Ghost Flag', hint: 'Muted tone-on-tone American flag (recommended)' },
  { value: 'hexagon', label: 'Navy Hexagon Pattern', hint: 'Repeating hexagonal grid, subtle silver lines' },
  { value: 'custom_image', label: 'Custom Image', hint: 'Your own image, auto-darkened for readability' },
];
const INTENSITIES = ['subtle', 'medium', 'bold'];
const FLAG_THEMES = [
  { value: 'classic',     label: 'Classic Red / White / Blue',  hint: 'Traditional American flag colors',           swatches: ['#b91c1c', '#f8fafc', '#1e3a8a'] },
  { value: 'navy_mono',   label: 'Navy Tone-on-Tone',           hint: 'Fully muted, subtle navy tones only',        swatches: ['#94a3b8', '#1e2952', '#f8fafc'] },
  { value: 'silver',      label: 'Silver Only',                 hint: 'Silver stripes, no color',                    swatches: ['#cbd5e1', '#334155', '#f8fafc'] },
  { value: 'gold_bronze', label: 'Gold + Bronze',               hint: 'Warm gold stripes with a bronze canton',     swatches: ['#b45309', '#fef3c7', '#78350f'] },
];

const BackgroundEditor = ({ settings, onSave, saving }) => {
  const [local, setLocal] = useState(settings.theme?.background || { variant: 'ghost_flag', intensity: 'subtle', flag_theme: 'classic' });
  useEffect(() => { setLocal(settings.theme?.background || { variant: 'ghost_flag', intensity: 'subtle', flag_theme: 'classic' }); }, [settings.theme?.background]);
  const dirty = JSON.stringify(local) !== JSON.stringify(settings.theme?.background || {});
  const save = () => onSave({ theme: { ...settings.theme, background: local } }, 'Background updated');
  const restore = () => setLocal({ variant: 'ghost_flag', intensity: 'subtle', flag_theme: 'classic', custom_image_url: null });

  return (
    <div className="space-y-4">
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-white">Website background</CardTitle>
          <p className="text-xs text-gray-400">Site stays primarily navy. All options are tone-on-tone and keep text readable.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {BG_VARIANTS.map((v) => {
              const active = local.variant === v.value;
              return (
                <button key={v.value} type="button" data-testid={`ws-bg-variant-${v.value}`}
                  onClick={() => setLocal((prev) => ({ ...prev, variant: v.value }))}
                  className={`text-left rounded-md p-3 transition-all border-2 ${active ? 'border-red-500 bg-slate-950' : 'border-slate-800 bg-slate-950/40 hover:border-slate-700'}`}>
                  <div className="text-white font-semibold text-sm">{v.label}</div>
                  <div className="text-[11px] text-gray-500 mt-1">{v.hint}</div>
                  {active && <div className="text-[10px] text-red-400 mt-1 uppercase tracking-widest">Active</div>}
                </button>
              );
            })}
          </div>

          {(local.variant === 'ghost_flag' || local.variant === 'hexagon') && (
            <div>
              <Label className="text-gray-300 text-xs uppercase tracking-widest mb-2 block">Intensity</Label>
              <div className="grid grid-cols-3 gap-2">
                {INTENSITIES.map((i) => (
                  <button key={i} type="button" data-testid={`ws-bg-intensity-${i}`}
                    onClick={() => setLocal((prev) => ({ ...prev, intensity: i }))}
                    className={`rounded-md py-2 text-sm capitalize border-2 transition-all ${local.intensity === i ? 'border-red-500 bg-slate-950 text-white' : 'border-slate-800 bg-slate-950/40 text-gray-400 hover:border-slate-700'}`}>{i}</button>
                ))}
              </div>
              <p className="text-[10px] text-gray-500 mt-2">Recommended default: Subtle.</p>
            </div>
          )}

          {local.variant === 'ghost_flag' && (
            <div>
              <Label className="text-gray-300 text-xs uppercase tracking-widest mb-2 block">Flag color theme</Label>
              <div className="grid grid-cols-2 gap-2">
                {FLAG_THEMES.map((t) => {
                  const active = (local.flag_theme || 'classic') === t.value;
                  return (
                    <button key={t.value} type="button" data-testid={`ws-bg-flag-theme-${t.value}`}
                      onClick={() => setLocal((prev) => ({ ...prev, flag_theme: t.value }))}
                      className={`text-left rounded-md p-3 transition-all border-2 flex items-center justify-between ${active ? 'border-red-500 bg-slate-950' : 'border-slate-800 bg-slate-950/40 hover:border-slate-700'}`}>
                      <div>
                        <div className="text-white font-semibold text-sm">{t.label}</div>
                        <div className="text-[10px] text-gray-500 mt-0.5">{active ? 'ACTIVE' : t.hint}</div>
                      </div>
                      <div className="flex gap-1">
                        {t.swatches.map((s, i) => <div key={i} className="w-5 h-5 rounded-full border border-slate-700" style={{ background: s }} />)}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {local.variant === 'custom_image' && (
            <div className="space-y-2">
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <Label className="text-gray-300 text-xs uppercase tracking-widest">Custom image URL</Label>
                  <Input data-testid="ws-bg-custom-url" value={local.custom_image_url || ''} onChange={(e) => setLocal((v) => ({ ...v, custom_image_url: e.target.value }))} placeholder="/api/uploads/backgrounds/… or https://…" className="bg-slate-800 border-slate-700 text-white font-mono text-sm" />
                </div>
                <BackgroundUploadButton onUploaded={(url) => setLocal((v) => ({ ...v, custom_image_url: url }))} />
              </div>
              {local.custom_image_url && (
                <div className="pt-2">
                  <span className="text-xs text-gray-500 block mb-1">Preview</span>
                  <div className="bg-slate-950 border border-slate-800 rounded overflow-hidden" style={{ maxHeight: '180px' }}>
                    <img src={local.custom_image_url} alt="Background preview" data-testid="ws-bg-custom-preview" className="w-full h-full object-cover" style={{ maxHeight: '180px' }} />
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={restore} data-testid="ws-bg-restore" className="border-slate-700 text-gray-300 hover:bg-slate-800">
          <RotateCcw className="w-4 h-4 mr-2" /> Restore standard
        </Button>
        <Button data-testid="ws-bg-save" onClick={save} disabled={saving || !dirty} className="bg-red-600 hover:bg-red-700 text-white">
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving…' : 'Save background'}
        </Button>
      </div>
    </div>
  );
};

// -------- Background upload button --------
const BackgroundUploadButton = ({ onUploaded }) => {
  const [uploading, setUploading] = useState(false);
  const inputRef = React.useRef(null);
  const pick = () => inputRef.current?.click();
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Image is too large (max 8 MB). Try a smaller file.');
      e.target.value = '';
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/api/admin/uploads/background`, fd, {
        headers: { ...authHeaders() },  // let axios set Content-Type for multipart
      });
      onUploaded(data.url);
      toast.success(data.resized_note || 'Image uploaded');
    } catch (err) {
      toast.error(`Upload failed: ${apiErr(err)}`);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };
  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        onChange={handleFile}
        className="hidden"
        data-testid="ws-bg-upload-input"
      />
      <Button
        type="button"
        onClick={pick}
        disabled={uploading}
        data-testid="ws-bg-upload-btn"
        className="bg-slate-800 hover:bg-slate-700 border border-slate-700 text-white h-10 shrink-0"
      >
        <Upload className="w-4 h-4 mr-2" />
        {uploading ? 'Uploading…' : 'Upload image'}
      </Button>
    </>
  );
};

// -------- Cursor --------
const CURSOR_STYLES = [
  { key: 'default', label: 'Default arrow', hint: 'Native browser pointer.' },
  { key: 'crosshair', label: 'Crosshair', hint: 'Native precision crosshair.' },
  { key: 'sniper', label: 'Sniper reticle', hint: 'Red reticle with center dot.' },
  { key: 'bullet_dot', label: 'Bullet dot', hint: 'Small red laser-style dot.' },
];
const TRAIL_SHAPES = [
  { key: 'hexagon', label: 'Hexagon' },
  { key: 'bullet_hole', label: 'Bullet hole' },
  { key: 'dot', label: 'Dot' },
  { key: 'star', label: 'Star' },
];
const TRAIL_COLORS = [
  { key: 'silver', label: 'Silver', hex: '#94a3b8' },
  { key: 'red',    label: 'Red',    hex: '#ef4444' },
  { key: 'gold',   label: 'Gold',   hex: '#eab308' },
  { key: 'green',  label: 'Green',  hex: '#22c55e' },
];
const TRAIL_SIZES = [
  { key: 'small', label: 'Small' },
  { key: 'medium', label: 'Medium' },
  { key: 'large', label: 'Large' },
];

const defaultCursorState = () => ({
  trail_enabled: true,
  cursor_style: 'default',
  trail_shape: 'hexagon',
  trail_color: 'silver',
  trail_size: 'medium',
  trail_intensity: 'subtle',
});

const CursorEditor = ({ settings, onSave, saving }) => {
  const [local, setLocal] = useState({ ...defaultCursorState(), ...(settings.theme?.cursor || {}) });
  useEffect(() => {
    setLocal({ ...defaultCursorState(), ...(settings.theme?.cursor || {}) });
  }, [settings.theme?.cursor]);
  const dirty = JSON.stringify(local) !== JSON.stringify(settings.theme?.cursor || {});
  const save = () => onSave({ theme: { ...settings.theme, cursor: local } }, 'Cursor updated');
  const setKey = (k) => (v) => setLocal((p) => ({ ...p, [k]: v }));
  return (
    <Card className="bg-slate-900 border-slate-800" data-testid="ws-cursor-editor">
      <CardHeader>
        <CardTitle className="text-white">Cursor &amp; trail effect</CardTitle>
        <p className="text-xs text-gray-400">
          Pick a pointer style and an optional trail. Both are auto-disabled on phones/tablets and for reduced-motion users, so this only affects desktop visitors.
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Cursor pointer style */}
        <div>
          <Label className="text-gray-300 text-xs uppercase tracking-widest mb-2 block">Cursor style</Label>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
            {CURSOR_STYLES.map((c) => (
              <button
                key={c.key}
                type="button"
                data-testid={`ws-cursor-pointer-${c.key}`}
                onClick={() => setKey('cursor_style')(c.key)}
                className={`text-left rounded-md p-3 border-2 transition-all ${
                  local.cursor_style === c.key
                    ? 'border-red-500 bg-slate-950 text-white'
                    : 'border-slate-800 bg-slate-950/40 text-gray-400 hover:border-slate-700'
                }`}
              >
                <div className="text-sm font-semibold">{c.label}</div>
                <div className="text-[11px] text-gray-500 mt-0.5">{c.hint}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Trail effect toggle */}
        <div className="flex items-center justify-between bg-slate-950/40 border border-slate-800 rounded-md p-3">
          <div>
            <div className="text-white text-sm font-semibold">Trail effect</div>
            <div className="text-[11px] text-gray-500">Off = plain pointer. On = small shapes fade behind the cursor on movement.</div>
          </div>
          <Switch
            data-testid="ws-cursor-toggle"
            checked={local.trail_enabled}
            onCheckedChange={(v) => setKey('trail_enabled')(v)}
          />
        </div>

        {local.trail_enabled && (
          <div className="space-y-5">
            {/* Shape */}
            <div>
              <Label className="text-gray-300 text-xs uppercase tracking-widest mb-2 block">Trail shape</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {TRAIL_SHAPES.map((s) => (
                  <button
                    key={s.key}
                    type="button"
                    data-testid={`ws-cursor-shape-${s.key}`}
                    onClick={() => setKey('trail_shape')(s.key)}
                    className={`flex flex-col items-center justify-center gap-1 rounded-md py-3 border-2 transition-all ${
                      local.trail_shape === s.key
                        ? 'border-red-500 bg-slate-950 text-white'
                        : 'border-slate-800 bg-slate-950/40 text-gray-400 hover:border-slate-700'
                    }`}
                  >
                    <ShapePreview shape={s.key} colorHex={(TRAIL_COLORS.find((c) => c.key === local.trail_color) || TRAIL_COLORS[0]).hex} />
                    <span className="text-xs">{s.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Color */}
            <div>
              <Label className="text-gray-300 text-xs uppercase tracking-widest mb-2 block">Trail color</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {TRAIL_COLORS.map((c) => (
                  <button
                    key={c.key}
                    type="button"
                    data-testid={`ws-cursor-color-${c.key}`}
                    onClick={() => setKey('trail_color')(c.key)}
                    className={`flex items-center gap-2 rounded-md py-2 px-3 border-2 transition-all ${
                      local.trail_color === c.key
                        ? 'border-red-500 bg-slate-950 text-white'
                        : 'border-slate-800 bg-slate-950/40 text-gray-400 hover:border-slate-700'
                    }`}
                  >
                    <span className="w-4 h-4 rounded-full border border-white/10" style={{ background: c.hex }} />
                    <span className="text-sm">{c.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Size */}
            <div>
              <Label className="text-gray-300 text-xs uppercase tracking-widest mb-2 block">Trail size</Label>
              <div className="grid grid-cols-3 gap-2">
                {TRAIL_SIZES.map((s) => (
                  <button
                    key={s.key}
                    type="button"
                    data-testid={`ws-cursor-size-${s.key}`}
                    onClick={() => setKey('trail_size')(s.key)}
                    className={`rounded-md py-2 text-sm border-2 transition-all ${
                      local.trail_size === s.key
                        ? 'border-red-500 bg-slate-950 text-white'
                        : 'border-slate-800 bg-slate-950/40 text-gray-400 hover:border-slate-700'
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Intensity (density) */}
            <div>
              <Label className="text-gray-300 text-xs uppercase tracking-widest mb-2 block">Trail intensity</Label>
              <div className="grid grid-cols-3 gap-2">
                {INTENSITIES.map((i) => (
                  <button
                    key={i}
                    type="button"
                    data-testid={`ws-cursor-intensity-${i}`}
                    onClick={() => setKey('trail_intensity')(i)}
                    className={`rounded-md py-2 text-sm capitalize border-2 transition-all ${
                      (local.trail_intensity || local.trail_style) === i
                        ? 'border-red-500 bg-slate-950 text-white'
                        : 'border-slate-800 bg-slate-950/40 text-gray-400 hover:border-slate-700'
                    }`}
                  >
                    {i}
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-gray-500 mt-2">Higher intensity = more shapes, denser trail.</p>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between pt-2 border-t border-slate-800">
          <p className="text-[11px] text-gray-500">Move your mouse anywhere outside the admin panel to preview your trail on the public site.</p>
          <Button
            data-testid="ws-cursor-save"
            onClick={save}
            disabled={saving || !dirty}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving…' : 'Save cursor'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const ShapePreview = ({ shape, colorHex }) => {
  const stroke = colorHex;
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">
      {shape === 'hexagon' && <polygon points="12,2 22,7 22,17 12,22 2,17 2,7" fill="none" stroke={stroke} strokeWidth="1.6" />}
      {shape === 'bullet_hole' && (
        <>
          <circle cx="12" cy="12" r="9" fill="none" stroke={stroke} strokeWidth="1.4" />
          <circle cx="12" cy="12" r="4.5" fill={stroke} opacity="0.55" />
        </>
      )}
      {shape === 'dot' && <circle cx="12" cy="12" r="6" fill={stroke} />}
      {shape === 'star' && (
        <polygon
          points="12,2 14.6,9 22,9.5 16.2,14.3 18.2,21.5 12,17.6 5.8,21.5 7.8,14.3 2,9.5 9.4,9"
          fill="none" stroke={stroke} strokeWidth="1.4"
        />
      )}
    </svg>
  );
};

// -------- History (snapshots / restore) --------
const HistoryPanel = ({ onRestored }) => {
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/site-settings/history`, { headers: authHeaders() });
      setSnapshots(data || []);
    } catch (err) {
      toast.error(`Failed to load: ${apiErr(err)}`);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const doRestore = async (id) => {
    setRestoring(id);
    try {
      const { data } = await axios.post(`${API}/api/admin/site-settings/restore/${id}`, {}, { headers: authHeaders() });
      onRestored?.(data);
      toast.success('Previous version restored');
      load();
    } catch (err) {
      toast.error(`Restore failed: ${apiErr(err)}`);
    } finally { setRestoring(null); }
  };

  return (
    <Card className="bg-slate-900 border-slate-800">
      <CardHeader>
        <CardTitle className="text-white">Version history</CardTitle>
        <p className="text-xs text-gray-400">Every time you save a change, a snapshot of the previous settings is saved here. Restore any one to roll back — a fresh snapshot is created first so a Restore itself can be undone. The 20 most recent snapshots are kept.</p>
      </CardHeader>
      <CardContent className="space-y-2">
        {loading && <p className="text-gray-400 text-sm">Loading…</p>}
        {!loading && !snapshots.length && <p className="text-gray-500 text-sm">No snapshots yet. Save any change to create one.</p>}
        {snapshots.map((s) => (
          <div key={s.id} data-testid={`ws-snapshot-${s.id}`} className="flex items-center justify-between bg-slate-950/40 border border-slate-800 rounded-md p-3">
            <div className="min-w-0 flex-1">
              <div className="text-white text-sm">{s.label}</div>
              <div className="text-[11px] text-gray-500 font-mono">{new Date(s.created_at).toLocaleString()}</div>
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="sm" variant="outline" data-testid={`ws-snapshot-restore-${s.id}`} disabled={restoring === s.id} className="border-amber-800 text-amber-300 hover:bg-amber-950">
                  <RotateCcw className="w-3 h-3 mr-2" /> Restore
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-slate-900 border-slate-800">
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-white">Restore this version?</AlertDialogTitle>
                  <AlertDialogDescription className="text-gray-400">
                    Your current settings will be replaced with the snapshot from <strong>{new Date(s.created_at).toLocaleString()}</strong>. The current version is saved as a fresh snapshot first, so this restore can itself be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="bg-slate-800 border-slate-700 text-white hover:bg-slate-700">Cancel</AlertDialogCancel>
                  <AlertDialogAction data-testid={`ws-snapshot-restore-confirm-${s.id}`} onClick={() => doRestore(s.id)} className="bg-red-600 hover:bg-red-700 text-white">Restore version</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};


const F = ({ label, children }) => (
  <div>
    <Label className="text-gray-300 text-xs uppercase tracking-widest">{label}</Label>
    {children}
  </div>
);

export default AdminWebsite;
